import { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField, makeStyles } from '@material-ui/core';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

const BU_ANNOTATION = 'graph.microsoft.com/business-unit-name';
const PU_ANNOTATION = 'graph.microsoft.com/organization-unit-name';

const useStyles = makeStyles(theme => ({
  textField: {
    backgroundColor: theme.palette.background.paper,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.text.primary,
      },
    },
  },
  select: {
    color: theme.palette.text.primary,
  },
}));

class AnnotationContainsFilter implements EntityFilter {
  constructor(
    readonly annotationKey: string,
    readonly value: string,
  ) {}

  filterEntity(entity: Entity): boolean {
    const annotationValue = entity.metadata.annotations?.[this.annotationKey];
    if (!annotationValue) {
      return false;
    }

    return (
      annotationValue.trim().toLocaleLowerCase() ===
      this.value.trim().toLocaleLowerCase()
    );
  }

  toQueryValue(): string {
    return this.value;
  }
}

type ExtendedEntityFilters = DefaultEntityFilters & {
  businessUnit?: AnnotationContainsFilter;
  productUnit?: AnnotationContainsFilter;
};

export const BusinessProductUnitFilter = () => {
  const classes = useStyles();
  const {
    backendEntities,
    filters,
    updateFilters,
    queryParameters: {
      businessUnit: businessUnitQueryParameter,
      productUnit: productUnitQueryParameter,
    },
  } = useEntityList<ExtendedEntityFilters>();

  const queryBusinessUnit = [businessUnitQueryParameter].flat()[0] ?? '';
  const queryProductUnit = [productUnitQueryParameter].flat()[0] ?? '';

  const [businessUnit, setBusinessUnit] = useState(
    queryBusinessUnit || filters.businessUnit?.value || '',
  );
  const [productUnit, setProductUnit] = useState(
    queryProductUnit || filters.productUnit?.value || '',
  );

  const distinctBusinessUnits = useMemo(() => {
    const values = new Set<string>();

    for (const entity of backendEntities) {
      const value = entity.metadata.annotations?.[BU_ANNOTATION]?.trim();
      if (value && value.toLocaleLowerCase() !== 'unknown') {
        values.add(value);
      }
    }

    if (businessUnit) {
      values.add(businessUnit);
    }

    return [...values].sort((a, b) => a.localeCompare(b));
  }, [backendEntities, businessUnit]);

  const distinctProductUnits = useMemo(() => {
    const values = new Set<string>();

    for (const entity of backendEntities) {
      const value = entity.metadata.annotations?.[PU_ANNOTATION]?.trim();
      if (value && value.toLocaleLowerCase() !== 'unknown') {
        values.add(value);
      }
    }

    if (productUnit) {
      values.add(productUnit);
    }

    return [...values].sort((a, b) => a.localeCompare(b));
  }, [backendEntities, productUnit]);

  useEffect(() => {
    const normalizedBusinessUnit = businessUnit.trim();
    const normalizedProductUnit = productUnit.trim();

    updateFilters({
      businessUnit: normalizedBusinessUnit
        ? new AnnotationContainsFilter(BU_ANNOTATION, normalizedBusinessUnit)
        : undefined,
      productUnit: normalizedProductUnit
        ? new AnnotationContainsFilter(PU_ANNOTATION, normalizedProductUnit)
        : undefined,
    });
  }, [businessUnit, productUnit, updateFilters]);

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <TextField
        label="Business Unit"
        select
        variant="outlined"
        margin="dense"
        className={classes.textField}
        value={businessUnit}
        onChange={event => setBusinessUnit(event.target.value)}
        inputProps={{ className: classes.select }}
      >
        <MenuItem value="">All</MenuItem>
        {distinctBusinessUnits.map(option => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Product Unit"
        select
        variant="outlined"
        margin="dense"
        className={classes.textField}
        value={productUnit}
        onChange={event => setProductUnit(event.target.value)}
        inputProps={{ className: classes.select }}
      >
        <MenuItem value="">All</MenuItem>
        {distinctProductUnits.map(option => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};